import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { useFetch } from "@/hooks/useFetch";
import { useSettings } from "@/context/SettingsContext";
import { fmtDate, fmtTime, money, todayISO } from "@/lib/format";
import { BRAND } from "@/lib/constants";

const ALL = "__all";

const JobCard = ({ j, index }) => (
  <article className="break-inside-avoid rounded-lg border border-slate-300 p-3 print:border-black" data-testid={`runsheet-job-${j.id}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div className="flex items-baseline gap-3">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[#0E1E38] text-xs font-bold text-white print:border print:border-black print:bg-white print:text-black">{index + 1}</span>
        <span className="font-mono text-lg font-bold">{fmtTime(j.start_time)}</span>
        <span className="font-mono text-sm text-slate-600">Job {j.job_number}</span>
        <span className="text-sm text-slate-600">{j.job_type}</span>
      </div>
      <span className="rounded bg-amber-100 px-2 py-0.5 font-mono text-sm font-bold text-amber-900 print:border print:border-black print:bg-white" data-testid={`runsheet-balance-${j.id}`}>Collect {money(j.remaining_balance)}</span>
    </div>
    <div className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
      <div><span className="font-semibold">{j.customer_name}</span> · <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{j.mobile}</span>{j.emergency_contact_number ? ` · Alt ${j.emergency_contact_number}` : ""}</div>
      <div>{j.men ? `${j.men} removalists` : ""}{j.truck_assigned ? ` · ${j.truck_assigned}` : ""}{j.cubic_metres ? ` · ${j.cubic_metres} m³` : ""}</div>
      <div><span className="text-xs font-semibold uppercase text-slate-500">Pickup</span><br />{j.pickup_address}</div>
      <div><span className="text-xs font-semibold uppercase text-slate-500">Delivery</span><br />{j.delivery_address}</div>
      {j.extra_stops?.map((s, i) => <div key={i}><span className="text-xs font-semibold uppercase text-slate-500">{s.type === "pickup" ? "Extra pickup" : "Extra drop-off"}</span><br />{s.address}</div>)}
      <div className="sm:col-span-2 text-xs text-slate-600">
        {`Deposit ${money(j.deposit_amount)} already paid · `}
        {j.pricing_type === "hourly" ? `Hourly ${money(j.hourly_rate)}/hr` : `Price ${money(j.total_estimate)}`}
        {j.driver_extras?.length ? ` · Extras: ${j.driver_extras.map((e) => `${e.type} ${money(e.amount)}`).join(", ")}` : ""}
      </div>
      {(j.special_requirements || j.job_notes) && <div className="sm:col-span-2 text-sm"><span className="text-xs font-semibold uppercase text-slate-500">Notes</span><br />{[j.special_requirements, j.job_notes].filter(Boolean).join(" — ")}</div>}
    </div>
  </article>
);

export default function RunSheetPage() {
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || todayISO();
  const team = params.get("team") || ALL;
  const { settings } = useSettings();
  const { data } = useFetch("/bookings", { date });
  const [printing, setPrinting] = useState(false);
  const jobs = useMemo(() => (data || []).filter((j) => j.status !== "Cancelled"), [data]);
  const teams = useMemo(() => [...new Set([...settings.teams, ...(settings.subbies || []), ...jobs.map((j) => j.assigned_team).filter(Boolean)])], [settings.teams, settings.subbies, jobs]);
  const groups = (team === ALL ? [...teams, ""] : [team]).map((t) => ({ team: t, jobs: jobs.filter((j) => (j.assigned_team || "") === t) })).filter((g) => g.jobs.length);

  const setParam = (k, v) => { const p = new URLSearchParams(params); p.set(k, v); setParams(p); };
  const print = () => { setPrinting(true); setTimeout(() => { window.print(); setPrinting(false); }, 50); };

  return (
    <div data-testid="runsheet-page">
      <div className="print:hidden">
        <Link to={`/calendar`} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="runsheet-back"><ArrowLeft className="h-4 w-4" /> Calendar</Link>
        <PageHeader title="Driver run sheets" subtitle="One sheet per driver with every job, address and balance to collect. Print or save as PDF.">
          <input type="date" value={date} onChange={(e) => e.target.value && setParam("date", e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="runsheet-date" />
          <select value={team} onChange={(e) => setParam("team", e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="runsheet-team">
            <option value={ALL}>All drivers</option>
            {teams.map((t) => <option key={t}>{t}</option>)}
            <option value="">Unassigned</option>
          </select>
          <Button onClick={print} disabled={printing || !groups.length} className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]" data-testid="runsheet-print"><Printer className="h-4 w-4" /> Print</Button>
        </PageHeader>
      </div>
      {groups.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500" data-testid="runsheet-empty">No jobs for this selection.</p>}
      <div className="space-y-8">
        {groups.map((g) => (
          <section key={g.team || "unassigned"} className="break-after-page rounded-xl border border-slate-200 bg-white p-5 print:border-0 print:p-0" data-testid={`runsheet-team-${g.team || "unassigned"}`}>
            <header className="mb-4 flex flex-wrap items-end justify-between gap-2 border-b-2 border-[#0E1E38] pb-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{BRAND} · Run sheet</div>
                <h2 className="font-display text-2xl font-bold text-[#0E1E38]">{g.team || "Unassigned jobs"}</h2>
              </div>
              <div className="text-right">
                <div className="font-semibold">{fmtDate(date, "EEEE d MMMM yyyy")}</div>
                <div className="text-sm text-slate-600">{g.jobs.length} job{g.jobs.length === 1 ? "" : "s"} · collect {money(g.jobs.reduce((s, j) => s + (j.remaining_balance || 0), 0))}</div>
              </div>
            </header>
            <div className="space-y-3">{g.jobs.map((j, i) => <JobCard key={j.id} j={j} index={i} />)}</div>
            <p className="mt-4 text-xs text-slate-500">Please call each customer approximately 30 minutes before arrival.</p>
          </section>
        ))}
      </div>
    </div>
  );
}
